/**
 * Variant group (storefront side): the sibling switcher. The product's group
 * is a set of separate products — the colors of a model — and the switcher
 * shows them all, this one marked, each of the others a link to its own page.
 *
 * The server sends (aldoo-na/store/product/extensions/variant-group):
 *
 *   product.variantGroup  { gid, axis, label, display: "image" | "color" | "label", value }
 *   product.siblings      [{ pid, name, thumbnail, contentType, value, inStock }]
 *
 * — the siblings the store offers only; one out of stock is shown, marked.
 * Where a sibling's page is is the project's: the slot takes `productUrl`
 * (sibling → href) from the page (`<ExtensionSlot name="siblings"
 * productUrl={…}>`), and falls back to the sibling's own `url`, then
 * `/product/<pid>`.
 */
import React from "react"
import { imageProps } from "aldoo-ra/CMS/url"

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" })

/** The server's order (siblings.js compare): the values' order, the label naturally, the pid. */
export const compareSiblings = (a, b) => {
  const orderA = Number.isFinite(a.value?.order) ? a.value.order : Infinity
  const orderB = Number.isFinite(b.value?.order) ? b.value.order : Infinity
  if (orderA !== orderB) return orderA < orderB ? -1 : 1
  const byLabel = collator.compare(a.value?.label || a.value?.key || "", b.value?.label || b.value?.key || "")
  if (byLabel) return byLabel
  return collator.compare(String(a.pid), String(b.pid))
}

const pictureOf = (item) => item.value?.image || item.thumbnail || null

/** This product and its siblings, in the switcher's order; nothing when it has none to switch to. */
export const switcherItems = (product) => {
  const group = product?.variantGroup
  const siblings = Array.isArray(product?.siblings) ? product.siblings.filter((sibling) => sibling?.pid && sibling.available !== false) : []
  if (!group?.gid || !siblings.length) return []
  const self = {
    pid: product.pid,
    name: product.name,
    thumbnail: product.thumbnail || product.square || product.thumb || null,
    value: group.value || { key: "", label: "" },
    inStock: product.in_stock !== false && !(typeof product.stock === "number" && product.stock <= 0),
    current: true,
  }
  return [self, ...siblings.filter((sibling) => sibling.pid !== product.pid)].sort(compareSiblings)
}

const hrefOf = (sibling, productUrl) => {
  if (typeof productUrl === "function") {
    const href = productUrl(sibling)
    if (href) return href
  }
  return sibling.url || `/product/${encodeURIComponent(sibling.pid)}`
}

// out of stock is marked by the strike, not by words: the framework has no text of its own here
const titleOf = (item) => item.value?.label || item.value?.key || item.name || ""

function Swatch({ item, display }) {
  const unavailable = item.inStock === false
  const strike = unavailable && <span aria-hidden className="absolute inset-0 pointer-events-none bg-[linear-gradient(to_top_right,transparent_calc(50%-1px),currentColor,transparent_calc(50%+1px))] opacity-60" />

  if (display === "color" && item.value?.hex) {
    return (
      <span className="relative block w-9 h-9 rounded-full overflow-hidden border border-black/10" style={{ background: item.value.hex }}>
        {strike}
      </span>
    )
  }
  if (display === "label") {
    return <span className="relative block px-3 py-1.5 text-sm leading-none whitespace-nowrap">{item.value?.label || item.value?.key || item.name}</span>
  }
  // "image", and a color without a hex: the sibling's picture (a color group keeps its round swatch)
  const picture = pictureOf(item)
  const box = display === "color" ? "w-9 h-9 rounded-full" : "w-14 h-14 rounded-md"
  return (
    <span className={`relative block ${box} overflow-hidden bg-black/5`}>
      {picture && <img {...imageProps(picture, "56px")} alt="" loading="lazy" className="w-full h-full object-cover" />}
      {!picture && item.value?.hex && <span className="absolute inset-0" style={{ background: item.value.hex }} />}
      {strike}
    </span>
  )
}

/** The switcher: the group's title with this product's value, then every member. */
export function SiblingSwitcher({ product, productUrl, className = "" }) {
  const items = switcherItems(product)
  if (!items.length) return null
  const group = product.variantGroup
  const display = group.display || "image"
  const current = group.value?.label || group.value?.key || ""
  const shape = display === "color" ? "rounded-full" : display === "label" ? "rounded-full" : "rounded-lg"

  return (
    <div className={`flex flex-col gap-2 ${className}`} data-testid="variant-group-switcher" data-display={display}>
      <div className="text-sm">
        <span className="font-medium">{group.label || group.axis}</span>
        {current && <span className="opacity-70">: {current}</span>}
      </div>
      <ul className="flex flex-wrap gap-2 list-none m-0 p-0">
        {items.map((item) => {
          const label = titleOf(item)
          const ring = item.current ? "ring-2 ring-offset-2 ring-current" : "ring-1 ring-black/10 hover:ring-black/40"
          const dim = item.inStock === false ? "opacity-50" : ""
          if (item.current) {
            return (
              <li key={item.pid}>
                <span aria-current="page" title={label} className={`block ${shape} ${ring} ${dim}`} data-testid="variant-group-current">
                  <Swatch item={item} display={display} />
                </span>
              </li>
            )
          }
          return (
            <li key={item.pid}>
              <a href={hrefOf(item, productUrl)} title={label} aria-label={label} className={`block ${shape} ${ring} ${dim} transition-shadow`} data-testid="variant-group-sibling">
                <Swatch item={item} display={display} />
              </a>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

export const StorefrontExtension = {
  id: "variant-group",
  label: "Variant group",
  slots: { siblings: SiblingSwitcher },
}

export default StorefrontExtension
